import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, AlertTriangle, ArrowLeft, Mail } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_ATTEMPTS = 10;      // 10 attempts × 2s = 20 seconds max
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const navigate = useNavigate();
  const { getAuthHeader, refreshUser } = useAuth();

  const [status, setStatus] = useState(sessionId ? 'checking' : 'error');
  const [attempts, setAttempts] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');

  // Use refs to avoid stale-closure bug — this is what caused the infinite hang.
  const attemptsRef = useRef(0);
  const cancelledRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) return;
    cancelledRef.current = false;
    poll();
    return () => {
      cancelledRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId]);

  const poll = async () => {
    if (cancelledRef.current) return;

    try {
      const { data } = await axios.get(
        `${API_URL}/api/payments/status/${sessionId}`,
        { headers: getAuthHeader(), timeout: 8000 }
      );

      if (cancelledRef.current) return;

      const paid = data?.payment_status === 'paid' || data?.status === 'complete';
      const expired = data?.status === 'expired';
      const failed = data?.payment_status === 'failed' || data?.status === 'failed';

      if (paid) {
        setStatus('success');
        try { await refreshUser(); } catch (_) { /* ignore refresh errors */ }
        toast.success('Payment successful!');
        return;
      }
      if (expired) {
        setStatus('error');
        setErrorMsg('This payment session has expired. Please start a new checkout.');
        toast.error('Payment session expired');
        return;
      }
      if (failed) {
        setStatus('error');
        setErrorMsg('Your payment was declined or failed. No charge was made.');
        toast.error('Payment failed');
        return;
      }

      // Still pending — retry with proper ref-based counter
      attemptsRef.current += 1;
      setAttempts(attemptsRef.current);

      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setStatus('timeout');
        return;
      }
      timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    } catch (error) {
      if (cancelledRef.current) return;
      const isAuthErr = error?.response?.status === 401;

      attemptsRef.current += 1;
      setAttempts(attemptsRef.current);

      if (isAuthErr || attemptsRef.current >= MAX_ATTEMPTS) {
        setStatus('error');
        setErrorMsg(
          isAuthErr
            ? 'Your session expired. Please sign in again — your payment is safe.'
            : 'We couldn\u2019t verify your payment right now. Check your email or contact support.'
        );
        return;
      }
      timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    }
  };

  const cancelPoll = () => {
    cancelledRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    navigate('/dashboard');
  };

  const progressPct = Math.min(100, (attempts / MAX_ATTEMPTS) * 100);

  return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center p-4" data-testid="payment-success-page">
      <div className="max-w-md w-full bg-brand-surface border border-white/10 rounded-2xl p-8 text-center">
        {status === 'checking' && (
          <>
            <div className="relative w-20 h-20 mx-auto mb-6">
              <div className="absolute inset-0 rounded-full border-4 border-brand-gold/10" />
              <Loader2 className="absolute inset-0 w-20 h-20 text-brand-gold animate-spin" strokeWidth={1.5} />
            </div>
            <h2 className="font-playfair text-2xl text-white mb-2">Confirming your payment</h2>
            <p className="text-sm text-gray-400 mb-4">
              Stripe is verifying the transaction. This normally takes just a few seconds.
            </p>
            <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden mb-2">
              <div
                className="h-full bg-brand-gold transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mb-6">
              Attempt {attempts + 1} of {MAX_ATTEMPTS}
            </p>
            <Button
              variant="outline"
              onClick={cancelPoll}
              className="border-white/10 text-gray-300 hover:bg-white/5 w-full"
              data-testid="cancel-polling-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Continue to Dashboard
            </Button>
            <p className="text-[11px] text-gray-500 mt-3">
              Don&apos;t worry — you&apos;ll get an email receipt once it clears.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-emerald-400" strokeWidth={1.5} />
            </div>
            <h2 className="font-playfair text-3xl text-white mb-2">Payment successful</h2>
            <p className="text-sm text-gray-400 mb-6">
              Thanks for the upgrade. Your access is unlocked and a receipt is on its way to your inbox.
            </p>
            <div className="space-y-2">
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-brand-gold text-black hover:brightness-110 font-semibold w-full"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/memberships')}
                className="border-white/10 text-gray-300 hover:bg-white/5 w-full"
                data-testid="view-membership-btn"
              >
                View Membership
              </Button>
            </div>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
              <Mail className="w-9 h-9 text-amber-400" strokeWidth={1.5} />
            </div>
            <h2 className="font-playfair text-2xl text-white mb-2">Still processing</h2>
            <p className="text-sm text-gray-400 mb-2">
              Stripe is taking a little longer than usual — this is normal for the first payment on a new card.
            </p>
            <p className="text-xs text-gray-500 mb-6">
              You&apos;ll receive an email confirmation once it settles (usually within a few minutes). Your membership will unlock automatically.
            </p>
            <div className="space-y-2">
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-brand-gold text-black hover:brightness-110 font-semibold w-full"
                data-testid="dashboard-btn"
              >
                Continue to Dashboard
              </Button>
              <Button
                variant="outline"
                onClick={() => { setAttempts(0); attemptsRef.current = 0; setStatus('checking'); poll(); }}
                className="border-white/10 text-gray-300 hover:bg-white/5 w-full"
                data-testid="retry-check-btn"
              >
                Check Again
              </Button>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-rose-500/15 border border-rose-500/30 flex items-center justify-center">
              <AlertTriangle className="w-10 h-10 text-rose-400" strokeWidth={1.5} />
            </div>
            <h2 className="font-playfair text-2xl text-white mb-2">Payment issue</h2>
            <p className="text-sm text-gray-400 mb-6">
              {errorMsg || (sessionId
                ? 'There was an issue verifying your payment. If you were charged, contact support and we\u2019ll sort it in minutes.'
                : 'No payment session was found. Please choose a plan to start checkout.')}
            </p>
            <div className="space-y-2">
              <Button
                onClick={() => navigate('/memberships')}
                className="bg-brand-gold text-black hover:brightness-110 font-semibold w-full"
                data-testid="try-again-btn"
              >
                {sessionId ? 'Try Again' : 'View Memberships'}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="border-white/10 text-gray-300 hover:bg-white/5 w-full"
                data-testid="back-to-dashboard-btn"
              >
                Back to Dashboard
              </Button>
              <a
                href="mailto:support@wemillionaires.com?subject=Payment%20Issue"
                className="block text-xs text-gray-500 hover:text-brand-gold pt-2"
                data-testid="contact-support-link"
              >
                Contact support &rarr;
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
